/**
 * RunDiagnosticsPanel (NC-458)
 *
 * Read-only live-state view for a single training run. Aggregates four
 * sections from `GET /api/runs/{runId}/diagnostics`:
 *   - RG        Azure resource-group existence
 *   - VMs       power/provisioning state per VM
 *   - AD        run OU/group member count (may be cached / error until the
 *               operator completes the DC RBAC + env prereq, see #458)
 *   - KV        `pwd-<runId>-*` secret names (admin-only; forbidden otherwise)
 *
 * Each section reports its own status so a partial failure (DC unreachable,
 * KV forbidden) never blanks the whole panel. Polls every 30s while mounted;
 * poll refreshes are silent (no full-panel spinner), only the initial load
 * shows the spinner.
 */

import React, { useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Tooltip,
  Alert,
} from '@mui/material';
import { Refresh as RefreshIcon, Cached as CachedIcon } from '@mui/icons-material';
import { runsApi } from '../services/api';
import { RunDiagnostics, RunPhaseTimelineEntry } from '../services/types';
import { useApiQuery } from '../hooks/useApiQuery';
import ErrorMessage from './ErrorMessage';
import { formatDateSafe } from '../utils/dateFormat';

const POLL_INTERVAL_MS = 30000;

type ChipColor = 'success' | 'warning' | 'error' | 'default' | 'info';

const RG_STATUS: Record<RunDiagnostics['rg']['status'], { color: ChipColor; label: string }> = {
  live: { color: 'success', label: 'Vorhanden' },
  'empty-or-missing': { color: 'warning', label: 'Leer / nicht vorhanden' },
  error: { color: 'error', label: 'Fehler' },
};

const SECTION_STATUS: Record<string, { color: ChipColor; label: string }> = {
  ok: { color: 'success', label: 'OK' },
  error: { color: 'error', label: 'Fehler' },
  forbidden: { color: 'default', label: 'Nur für Admins' },
};

// NC-1236: per-phase outcome → chip color/label for the provisioning timeline.
const PHASE_STATUS: Record<RunPhaseTimelineEntry['status'], { color: ChipColor; label: string }> = {
  success: { color: 'success', label: 'OK' },
  warning: { color: 'warning', label: 'Warnung' },
  error: { color: 'error', label: 'Fehler' },
};

function StatusChip({ color, label }: { color: ChipColor; label: string }) {
  return <Chip size="small" color={color} label={label} />;
}

/** Human-readable phase duration: prefer the reported `durationMs`, else derive it from start/end. */
function formatPhaseDuration(entry: RunPhaseTimelineEntry): string {
  const ms = entry.durationMs ?? (new Date(entry.end).getTime() - new Date(entry.start).getTime());
  if (!Number.isFinite(ms) || ms < 0) return '—';
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`;
}

export function RunDiagnosticsPanel({ runId }: { runId: string }) {
  const { data, loading, error, refetch } = useApiQuery<RunDiagnostics>(
    () => runsApi.diagnostics(runId),
    [runId],
    { enabled: !!runId }
  );

  // Silent 30s polling while the tab is mounted. refetch() flips `loading`, but
  // the spinner is guarded on `!data` so poll refreshes don't flash the panel.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    const id = setInterval(() => refetchRef.current(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [runId]);

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <ErrorMessage
        message={error}
        title="Diagnose konnte nicht geladen werden"
        onRetry={refetch}
      />
    );
  }

  if (!data) return null;

  const { rg, vms, ad, kvSecrets, timeline } = data;
  const rgStatus = RG_STATUS[rg.status];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Live-Zustand · zuletzt geprüft {formatDateSafe(data.observedAtUtc, 'dd.MM.yyyy HH:mm:ss')}
          {' '}(Auto-Refresh alle 30s)
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={refetch}>
          Aktualisieren
        </Button>
      </Box>

      {/* NC-1236: prominent failure reason (mirrored from the run doc by the BE). */}
      {data.provisioningError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="provisioning-error">
          <Typography variant="subtitle2" component="span">Provisionierung fehlgeschlagen: </Typography>
          {data.provisioningError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Provisioning phase timeline (NC-1236 V3) */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">Provisionierungs-Verlauf</Typography>
                <StatusChip {...SECTION_STATUS[timeline.status]} />
              </Box>
              {timeline.status === 'error' ? (
                <Typography variant="caption" color="error">
                  {timeline.error || 'Verlauf nicht verfügbar (Log Analytics nicht erreichbar).'}
                </Typography>
              ) : timeline.items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Noch keine Phasen-Ereignisse. Log Analytics hat eine Ingestion-Verzögerung von
                  einigen Minuten — bei laufender Provisionierung kurz warten und aktualisieren.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Phase</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell>Dauer</TableCell>
                      <TableCell align="right">Ereignisse</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timeline.items.map((entry) => (
                      <TableRow key={entry.scriptKey}>
                        <TableCell>
                          {entry.phase}
                          {entry.phase !== entry.scriptKey && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1, fontFamily: 'monospace' }}
                            >
                              {entry.scriptKey}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusChip {...PHASE_STATUS[entry.status]} />
                        </TableCell>
                        <TableCell>{formatDateSafe(entry.start, 'HH:mm:ss')}</TableCell>
                        <TableCell>{formatPhaseDuration(entry)}</TableCell>
                        <TableCell align="right">{entry.events}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Resource Group */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">Ressourcengruppe</Typography>
                <StatusChip {...rgStatus} />
              </Box>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {rg.name || '—'}
              </Typography>
              {rg.error && (
                <Typography variant="caption" color="error" display="block" mt={1}>
                  {rg.error}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Active Directory */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">Active Directory</Typography>
                <StatusChip {...SECTION_STATUS[ad.status]} />
              </Box>
              {ad.status === 'ok' ? (
                <>
                  <Typography variant="body1">
                    {ad.userCount ?? 0} Benutzer in der Gruppe
                  </Typography>
                  {ad.groupSam && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Gruppe: {ad.groupSam}
                    </Typography>
                  )}
                  {ad.fromCache && (
                    <Tooltip title="Aus dem 60s-Cache des Servers — kann leicht veraltet sein">
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CachedIcon />}
                        label="aus Cache"
                        sx={{ mt: 1 }}
                      />
                    </Tooltip>
                  )}
                </>
              ) : (
                <Typography variant="caption" color="error">
                  {ad.error || 'AD-Status nicht verfügbar (DC-Berechtigung/Env ausstehend).'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* VMs */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">
                  Virtuelle Maschinen ({vms.items.length})
                </Typography>
                <StatusChip {...SECTION_STATUS[vms.status]} />
              </Box>
              {vms.status === 'error' ? (
                <Typography variant="caption" color="error">
                  {vms.error || 'VM-Status nicht verfügbar.'}
                </Typography>
              ) : vms.items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Keine VMs in dieser Ressourcengruppe.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Größe</TableCell>
                      <TableCell>Power-State</TableCell>
                      <TableCell>Provisioning</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vms.items.map((vm) => (
                      <TableRow key={vm.name}>
                        <TableCell>{vm.name}</TableCell>
                        <TableCell>{vm.vmSize || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={vm.powerState === 'VM running' ? 'success' : 'default'}
                            label={vm.powerState || 'unbekannt'}
                          />
                        </TableCell>
                        <TableCell>{vm.provisioningState || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* KeyVault secrets */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">
                  KeyVault-Secrets ({kvSecrets.items.length})
                </Typography>
                <StatusChip {...SECTION_STATUS[kvSecrets.status]} />
              </Box>
              {kvSecrets.status === 'forbidden' ? (
                <Typography variant="body2" color="text.secondary">
                  Secret-Namen sind nur für Administratoren sichtbar.
                </Typography>
              ) : kvSecrets.status === 'error' ? (
                <Typography variant="caption" color="error">
                  {kvSecrets.error || 'KeyVault-Status nicht verfügbar.'}
                </Typography>
              ) : kvSecrets.items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Keine Secrets für diesen Run gefunden.
                </Typography>
              ) : (
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {kvSecrets.items.map((name) => (
                    <Chip key={name} size="small" variant="outlined" label={name} sx={{ fontFamily: 'monospace' }} />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default RunDiagnosticsPanel;
